import { useEffect, useState } from "react";
import { api, formatINR, formatDate } from "../lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, Empty } from "../components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { IndianRupee, Receipt, Building2, TrendingUp } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#3B82F6", "#8B5CF6"];

export default function Analytics() {
  const [days, setDays] = useState("365");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/analytics/spend", { params: { days } }).then((r) => setData(r.data)).catch(() => setData({
      total_spend: 0, avg_po_value: 0, active_vendors: 0, yoy_growth: 0,
      monthly_trend: [], spend_by_category: [], top_vendors: [], pos: [],
    }));
  }, [days]);

  if (!data) {
    return (
      <div>
        <PageHeader title="Spend Analytics" />
        <div className="grid grid-cols-4 gap-4 mb-6">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Spend Analytics"
        subtitle="Track procurement spend across vendors and categories"
        actions={[
          <Select key="dr" value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44 h-9" data-testid="analytics-date-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 1 year</SelectItem>
              <SelectItem value="730">Last 2 years</SelectItem>
            </SelectContent>
          </Select>
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Spend" value={formatINR(data.total_spend)} icon={IndianRupee} testid="analytics-total" />
        <StatCard label="Avg PO Value" value={formatINR(data.avg_po_value)} icon={Receipt} accent="#3B82F6" />
        <StatCard label="Active Vendors" value={data.active_vendors} icon={Building2} accent="#10B981" />
        <StatCard label="YoY Growth" value={data.yoy_growth == null ? "—" : `${data.yoy_growth > 0 ? "+" : ""}${data.yoy_growth}%`} icon={TrendingUp} accent={(data.yoy_growth || 0) >= 0 ? "#10B981" : "#EF4444"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
        <SectionCard title="Monthly Spend Trend" testid="trend-chart">
          <div className="h-64">
            {(data.monthly_trend || []).length === 0 ? (
              <Empty title="No spend in this period" subtitle="Try a wider date range" />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Line type="monotone" dataKey="amount" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        </div>

        <SectionCard title="Spend by Category" testid="category-pie">
          <div className="h-64">
            {(data.spend_by_category || []).length === 0 ? (
              <Empty title="No category spend" subtitle="No purchase orders in this range" />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.spend_by_category} dataKey="amount" nameKey="category" cx="50%" cy="42%" innerRadius={45} outerRadius={70} paddingAngle={2}>
                  {data.spend_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Top 10 Vendors by Spend" testid="top-vendors">
        {(data.top_vendors || []).length === 0 ? (
          <Empty title="No vendor spend" subtitle="No purchase orders in this range" />
        ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.top_vendors} layout="vertical" margin={{ left: 110 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
              <YAxis type="category" dataKey="vendor_name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={100} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Bar dataKey="amount" fill="#6366F1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </SectionCard>

      <SectionCard title="Recent Purchase Orders" testid="po-table">
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-5 py-2">Vendor</th>
                <th className="py-2">Category</th>
                <th className="py-2">Amount</th>
                <th className="py-2">Date</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.pos || []).length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-zinc-500">No purchase orders in this range</td></tr>
              )}
              {(data.pos || []).slice(0, 15).map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="px-5 py-2.5 font-medium">{p.vendor_name}</td>
                  <td>{p.category}</td>
                  <td>{formatINR(p.amount)}</td>
                  <td>{formatDate(p.date)}</td>
                  <td><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
